import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/Typography';
import { formatBRL } from '@/lib/format';
import { walletNumbers, type WalletOut } from '@/api/wallet';

/**
 * Cards da tela Carteira — redesign "Bow Carteiras" (T1).
 * - WalletBalanceCard: card Saldo principal (estado devedor com valor negativo
 *   em vermelho, CTA "Adicionar saldo"). Sem reserva: o saldo aparece cheio
 *   mesmo durante a recarga — o débito só acontece no fim.
 * - WalletCashbackCard: carteira cashback com borda-gradiente do espectro e
 *   badge `$` roxo. Sem CTA — cashback "só se ganha".
 * - WalletMovementsRow: linha "Ver movimentações" → /history.
 */

const CASHBACK_TEXT = '#A23C92';
const CASHBACK_BADGE_GRADIENT = 'linear-gradient(135deg,#835DD0,#C04CAE)';
const SPECTRUM_GRADIENT =
  'linear-gradient(135deg,#EE263E,#DE7C00,#FFB92A,#0090E2,#835DD0,#C04CAE)';

interface BalanceProps {
  wallet: WalletOut | null;
}

export function WalletBalanceCard({ wallet }: BalanceProps) {
  const navigate = useNavigate();

  if (!wallet) {
    return (
      <Card className="flex flex-col gap-3 p-[22px]">
        <div className="h-3 w-28 animate-pulse rounded bg-bow-royal/10" />
        <div className="h-10 w-44 animate-pulse rounded bg-bow-royal/10" />
        <div className="mt-3 h-12 w-full animate-pulse rounded-full bg-bow-royal/10" />
      </Card>
    );
  }

  const { available, debt, minTopup } = walletNumbers(wallet);
  const inDebt = debt > 0;
  // O backend já resolve o mínimo do aporte (dívida + mínimo pra voltar a
  // operar) em `min_topup_brl`. O `max` com a dívida é só rede de segurança:
  // prometer menos que a dívida faria o PIX não quitar a pendência.
  const minTopupBrl = Math.max(minTopup, debt);

  return (
    <Card className="flex flex-col gap-3 p-[22px] [@media(max-height:740px)]:gap-2 [@media(max-height:740px)]:p-5">
      <span
        className="text-[11.5px] font-semibold uppercase tracking-[0.06em]"
        style={{ color: inDebt ? '#D5152B' : 'rgba(0,27,116,0.55)' }}
      >
        Saldo principal
      </span>

      {inDebt ? (
        <MoneyNeg value={debt} size={50} />
      ) : (
        <MoneyDisplay
          value={available}
          size={50}
          className="[@media(max-height:740px)]:!text-[40px]"
        />
      )}

      {inDebt && (
        <span className="text-xs" style={{ color: 'rgba(0,27,116,0.6)' }}>
          O próximo aporte cobre esse valor — mínimo {formatBRL(minTopupBrl)}.
        </span>
      )}

      <Button
        variant="primary"
        className="mt-2 [@media(max-height:740px)]:mt-1"
        onClick={() => navigate('/wallet/recharge')}
      >
        <PlusIcon /> Adicionar saldo
      </Button>
    </Card>
  );
}

interface CashbackProps {
  wallet: WalletOut | null;
}

export function WalletCashbackCard({ wallet }: CashbackProps) {
  // Disponível, não o total. Desde a migration 050 nada é reservado e os dois
  // são iguais; o campo fica por compat com bundle antigo/backend antigo.
  const { bonusAvailable } = walletNumbers(wallet);

  return (
    <div
      className="rounded-[22px] shadow-soft"
      style={{ padding: '1.5px', background: SPECTRUM_GRADIENT }}
    >
      <div className="flex items-center justify-between rounded-[21px] bg-white px-[18px] py-4">
        <div className="flex flex-col gap-1">
          <span
            className="text-[11.5px] font-semibold uppercase tracking-[0.06em]"
            style={{ color: CASHBACK_TEXT }}
          >
            Carteira cashback
          </span>
          <MoneyDisplay value={bonusAvailable} size={26} style={{ color: CASHBACK_TEXT }} />
        </div>
        <span
          aria-hidden
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          style={{ background: CASHBACK_BADGE_GRADIENT }}
        >
          <span
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 700,
              fontSize: 20,
              lineHeight: 1,
              color: '#fff'
            }}
          >
            $
          </span>
        </span>
      </div>
    </div>
  );
}

export function WalletMovementsRow() {
  return (
    <Link
      to="/history"
      className="flex items-center gap-3 rounded-[20px] bg-white shadow-soft transition hover:bg-bow-cream/40"
      style={{ padding: '15px 18px' }}
    >
      <ListIcon />
      <span className="flex-1 text-[14.5px] font-semibold text-bow-royal">
        Ver movimentações
      </span>
      <ChevronRightIcon />
    </Link>
  );
}

/**
 * Linha "Cartões salvos" → /wallet/cards (T3). Só aparece quando o backend
 * expõe a rota de cartões: enquanto `card_save_enabled` estiver desligada,
 * a Carteira segue exatamente como está hoje.
 */
export function WalletCardsRow({ count }: { count: number }) {
  return (
    <Link
      to="/wallet/cards"
      className="flex items-center gap-3 rounded-[20px] bg-white shadow-soft transition hover:bg-bow-cream/40"
      style={{ padding: '15px 18px' }}
    >
      <CardIcon />
      <span className="flex-1 text-[14.5px] font-semibold text-bow-royal">Cartões salvos</span>
      {count > 0 && (
        <span className="text-[13px] font-semibold" style={{ color: 'rgba(0,27,116,0.45)' }}>
          {count}
        </span>
      )}
      <ChevronRightIcon />
    </Link>
  );
}

/**
 * Valor negativo em vermelho no formato `− R$ 23,40` — MoneyDisplay não
 * suporta negativo (Math.floor arredonda pra baixo). Recebe o valor da
 * pendência como número POSITIVO e prefixa o `−`.
 */
function MoneyNeg({ value, size = 50 }: { value: number; size?: number }) {
  const abs = Math.abs(value);
  const cents = Math.round((abs % 1) * 100)
    .toString()
    .padStart(2, '0');
  const reais = Math.floor(abs).toLocaleString('pt-BR');
  return (
    <span
      className="[@media(max-height:740px)]:!text-[40px]"
      style={{
        fontFamily: 'Chillax, system-ui, sans-serif',
        fontWeight: 600,
        fontSize: size,
        lineHeight: 1,
        letterSpacing: '-0.03em',
        color: '#EE263E'
      }}
    >
      <span style={{ fontSize: size * 0.6, fontWeight: 500, marginRight: 4 }}>−</span>
      <span style={{ fontSize: size * 0.5, fontWeight: 500, marginRight: 4 }}>R$</span>
      {reais}
      <span style={{ fontSize: size * 0.5, fontWeight: 500 }}>,{cents}</span>
    </span>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="#001B74"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="#001B74"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
      <path d="M2.5 10h19" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="rgba(0,27,116,0.4)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
